import { Injectable, signal } from '@angular/core';
import { signUp, signIn, confirmSignUp, getCurrentUser, fetchUserAttributes, fetchAuthSession, JWT, signOut, updateUserAttributes, resetPassword, confirmResetPassword, resendSignUpCode } from 'aws-amplify/auth';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UserProfileDto, createUserProfileDto } from './user.dto';
import { firstValueFrom, map } from 'rxjs';

interface IUser {
  id: string;
  email: string;
  token: string | JWT;
  userName: string | null;
  userProfile: UserProfileDto | null;
}

interface ICreateUserDto {
  userName: string;
  email: string;
  password: string;
}

type AuthState = {
  user: IUser | null
}

const defaultRegisterUser = {
  email: '',
  userName: '',
  password: ''
}

type IRegistrationState = 'signup' | 'confirm' | 'forgot';
export type IAuthFormType = 'login' | 'register' | 'forgot' | 'confirm';

@Injectable({
  providedIn: 'root'
})

export class AuthService {

  user = signal<IUser | null>(null);
  registerUser = signal<ICreateUserDto>(defaultRegisterUser)
  registrationStep = signal<IRegistrationState>('signup');
  errorMessages = signal<string | null>(null);
  passwordRequested = signal<boolean>(false);
  currentForm = signal<IAuthFormType>('login');
  loading = signal<boolean>(false);


  constructor(private router: Router, private http: HttpClient) { }

  async register(user: ICreateUserDto): Promise<boolean> {

    const { userName, password, email } = user;
    try {
      const { nextStep, userId } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      })

      if (!userId && nextStep.signUpStep !== 'CONFIRM_SIGN_UP') {
        throw new Error('registration failed')
      }
      this.registrationStep.set('confirm');
      this.registerUser.set({
        email,
        userName,
        password
      })

      return true;

    }
    catch (e: any) {
      this.errorMessages.set(e.message)
      this.loading.set(false);
      return false
    }
  }

  createUserProfile = (user: createUserProfileDto) => this.http.post<{ user: UserProfileDto }>('/api/users/userprofile', user).pipe(map((userProfile: { user: UserProfileDto }) => userProfile.user));

  async confirmRegistration(code: string, emailOverride?: string) {
    this.loading.set(true);
    try {
      const { email: storedEmail, userName, password } = this.registerUser();
      const email = emailOverride || storedEmail;

      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode: code
      })

      await signOut()
      await signIn({ username: email, password })
      const user = await getCurrentUser();
      const attr = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const userProfile = await firstValueFrom(this.createUserProfile({ email: email, cognitoSub: user.userId }));

      const newUser: IUser = {
        email: user.username ?? '',
        id: user.userId,
        token: session?.tokens?.accessToken.toString() ?? '',
        userName: attr.preferred_username ?? null,
        userProfile: userProfile ?? null
      }

      this.user.set(newUser);
      this.router.navigate(['/'])

      if (!user) return null;

      return isSignUpComplete && user ? user : null

    } catch (e: any) {
      if (e.name === 'ExpiredCodeException' || e.name === 'CodeExpiredException') {
        const email = emailOverride || this.registerUser().email;
        this.errorMessages.set('Your code has expired. Sending a new one...');
        await resendSignUpCode({ username: email }).catch(() => {});
        this.errorMessages.set('Your code expired — a new one has been sent to your email.');
      } else {
        this.errorMessages.set(e.message);
      }
      this.loading.set(false);
      return false;
    }
  }

  async resendCode(email: string): Promise<void> {
    try {
      this.loading.set(true);
      await resendSignUpCode({ username: email });
      this.errorMessages.set('A new confirmation code has been sent to your email.');
      this.loading.set(false);
    } catch (e: any) {
      this.errorMessages.set(e.message);
      this.loading.set(false);
    }
  }

  async isAuthenticated() {
    const user = this.fetchUser();
    if (await user) {
      return true;
    }
    return false
  }

  fetchUserProfile = () => this.http.get<{ user: UserProfileDto }>('/api/users/userprofile').pipe(map((userProfile: { user: UserProfileDto }) => userProfile.user));

  async fetchUser(): Promise<IUser | null> {

    try {
      this.loading.set(true);
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        this.loading.set(false);
        return null;
      }
      const user = await getCurrentUser();
      if (!user) {
        this.loading.set(false);
        return null;
      }
      const attr = await fetchUserAttributes();
      const userProfile = await firstValueFrom(this.fetchUserProfile());

      const newUser: IUser = {
        email: user.username ?? '',
        id: user.userId,
        token: session?.tokens?.accessToken.toString() ?? '',
        userName: attr.preferred_username ?? null,
        userProfile: userProfile
      }

      this.loading.set(false);
      return newUser;

    }
    catch (e: any) {
      this.errorMessages.set(e.message)
      this.loading.set(false);
      return null
    }

  }


  async loginUser(email: string, password: string) {

    try {
      this.loading.set(true);
      await signOut()
      await signIn({ username: email, password })
      if (!this.isAuthenticated) throw (new Error('could not login'))

      const user = await this.fetchUser();
      if (!user?.userProfile) {
        await signOut();
        throw (new Error('could not fetch user profile'))
      }
      this.user.set(user);
      this.router.navigate(['/']);
      this.loading.set(false);
    }
    catch (e: any) {
      this.errorMessages.set(e.message)
      this.loading.set(false);
    }

  }

  async logOut() {
    try {
      this.loading.set(true);
      await signOut();
      this.user.set(null);
      this.registerUser.set(defaultRegisterUser);
      this.errorMessages.set(null);
      this.currentForm.set('login');
      this.registrationStep.set('signup');
      this.passwordRequested.set(false);
      this.loading.set(false);
      this.router.navigate(['/auth']);
    } catch (e) {
      this.loading.set(false);
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await resetPassword({ username: email });
      this.passwordRequested.set(true);
    } catch (e: any) {
      this.errorMessages.set('An error occurred. Please try again later.');
      throw e;
    }
  }

  async setNewPassword(email: string, code: string, newPassword: string): Promise<void> {
    try {
      this.loading.set(true);
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword }).then((result: any) => {
        this.currentForm.set('login');
        this.passwordRequested.set(false);
        this.errorMessages.set("password reset successfully");
      });

      this.loading.set(false);

    } catch (e: any) {
      this.errorMessages.set(e.message)
      this.loading.set(false);
      throw e;
    }
  }

  setCurrentForm(mode: IAuthFormType) {
    this.currentForm.set(mode);
  }


}